import { createDecorator } from 'vue-class-component';

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

var DEPENDENCIES_METADATA_KEY = "__dependencies__";
function makeDecorator(handler) {
    return function (target, propertyKey, parameterIndex) {
        if (parameterIndex != null) {
            if (!Reflect.hasMetadata(DEPENDENCIES_METADATA_KEY, target)) {
                Reflect.defineMetadata(DEPENDENCIES_METADATA_KEY, [], target);
            }
            var dependencies = Reflect.getMetadata(DEPENDENCIES_METADATA_KEY, target);
            var dependency = dependencies[parameterIndex] || {};
            handler(dependency, target, propertyKey, parameterIndex);
            dependencies[parameterIndex] = dependency;
        }
        else {
            createDecorator(function (options, key) {
                if (options.dependencies == null) {
                    options.dependencies = {};
                }
                var dependency = options.dependencies[key] || {};
                handler(dependency, target, propertyKey, parameterIndex);
                options.dependencies[key] = dependency;
            })(target, propertyKey);
        }
    };
}
function typeFromMetadata(target, propertyKey, parameterIndex) {
    if (parameterIndex != null) {
        var paramtypes = Reflect.getMetadata("design:paramtypes", target) || [];
        return paramtypes[parameterIndex] || null;
    }
    else {
        return Reflect.getMetadata("design:type", target, propertyKey) || null;
    }
}
function dependencyFromMetadata(target, parameterIndex) {
    var dependencies = Reflect.getMetadata(DEPENDENCIES_METADATA_KEY, target) || [];
    return dependencies[parameterIndex] || null;
}

var Injector =  (function () {
    function Injector(providers, parent) {
        var _this = this;
        this._tokenProviderMap = new Map();
        this._tokenInstanceMap = new Map();
        providers.forEach(function (provider) {
            _this._tokenProviderMap.set(typeof provider === "function" ? provider : provider.provide, provider);
        });
        this.parent = parent || null;
    }
    Injector.prototype.get = function (token, notFoundValue) {
        if (notFoundValue === void 0) { notFoundValue = Injector.THROW_IF_NOT_FOUND; }
        return this.getByOption({
            token: token,
            optional: notFoundValue !== Injector.THROW_IF_NOT_FOUND,
            notFoundValue: notFoundValue
        });
    };
    Injector.prototype.getByOption = function (option) {
        var token = option.token, optional = option.optional, notFoundValue = option.notFoundValue;
        if (token == null) {
            throw new Error("Token should not be `undefined` or `null`");
        }
        if (this._tokenInstanceMap.has(token)) {
            return this._tokenInstanceMap.get(token);
        }
        if (this._tokenProviderMap.has(token)) {
            var provider = this._tokenProviderMap.get(token);
            var instance = this.resolveProviderInstance(provider);
            this._tokenInstanceMap.set(token, instance);
            return instance;
        }
        if (token === Injector) {
            return this;
        }
        if (this.parent != null) {
            return this.parent.getByOption(option);
        }
        if (optional) {
            return notFoundValue;
        }
        else {
            throw new Error("Provider is not found.");
        }
    };
    Injector.prototype.resolveProviderInstance = function (provider) {
        if (typeof provider === "function") {
            return this.resolveTypeInstance(provider);
        }
        else if ("useValue" in provider) {
            return provider.useValue;
        }
        else if ("useClass" in provider) {
            return this.resolveTypeInstance(provider.useClass);
        }
        else if ("useExisting" in provider) {
            return this.get(provider.useExisting);
        }
        else if ("useFactory" in provider) {
            var _a = provider, useFactory = _a.useFactory, deps = _a.deps;
            return this.resolveFactoryInstance(useFactory, deps);
        }
    };
    Injector.prototype.resolveTypeInstance = function (type) {
        var paramInstances = [];
        for (var i = 0; i < type.length; ++i) {
            var dependency = dependencyFromMetadata(type, i);
            if (dependency != null) {
                paramInstances.push(this.getByOption(dependency));
                continue;
            }
            var paramType = typeFromMetadata(type, "", i);
            if (paramType != null) {
                paramInstances.push(this.get(paramType));
                continue;
            }
            throw new Error("ParamTypes metadata is not found. Do you forget `@injectable()` for class " + type.name + "?");
        }
        return new (type.bind.apply(type, __spreadArrays([void 0], paramInstances)))();
    };
    Injector.prototype.resolveFactoryInstance = function (factory, deps) {
        var _this = this;
        if (deps == null) {
            deps = [];
        }
        var depInstances = deps.map(function (dep) {
            if (dep == null) {
                throw new Error("Dependency token should not be `undefined` or `null`.");
            }
            return _this.get(dep);
        });
        return factory.apply(void 0, depInstances);
    };
    Injector.THROW_IF_NOT_FOUND = new Object();
    return Injector;
}());

var injectableDecorator = function (target) { return target; };
function injectable() {
    return injectableDecorator;
}
function inject(token) {
    return makeDecorator(function (dependency, target, propertyKey, parameterIndex) {
        if (token == null) {
            var type = typeFromMetadata(target, propertyKey, parameterIndex);
            if (type == null) {
                throw new Error("Type metadata is not found.");
            }
            token = type;
        }
        dependency.token = token;
    });
}
function optional() {
    return makeDecorator(function (dependency, target, propertyKey, parameterIndex) {
        dependency.optional = true;
        dependency.notFoundValue = null;
    });
}

var InjectionToken =  (function () {
    function InjectionToken(desc) {
        this._desc = desc;
    }
    InjectionToken.prototype.toString = function () {
        return "InjectionToken " + (this._desc != null ? this._desc : "?");
    };
    return InjectionToken;
}());

var VueTypeScriptInject =  (function () {
    function VueTypeScriptInject() {
    }
    VueTypeScriptInject.install = function (_Vue) {
        _Vue.mixin({
            beforeCreate: function () {
                var _this = this;
                var providers = this.$options.providers || [];
                var parent = this.$parent != null ? this.$parent.$injector : null;
                this.$injector = new Injector(providers, parent);
                if (this.$options.dependencies != null) {
                    var dependencies_1 = this.$options.dependencies;
                    Object.keys(dependencies_1).forEach(function (propertyKey) {
                        _this[propertyKey] = _this.$injector.getByOption(dependencies_1[propertyKey]);
                    });
                }
            }
        });
    };
    return VueTypeScriptInject;
}());

export default VueTypeScriptInject;
export { InjectionToken, Injector, inject, injectable, optional };
